import { WEBXR_TO_NUE } from "../webxr-nue-basis.js";
import { mintQrGeoPose } from "./qr-geo-pose-minting.js";
import { serializeQrLevel } from "./qr-level.js";
import { Matrix4, Quaternion, Vector3 } from "three";
//#region ../src/ar/qr/qr-mint-level.ts
/**
* Minting a QR level from a solved pose: the odometry → GPS-world
* composition, and the level/quality assembly around `mintQrGeoPose`.
*
* WHY THIS IS FRAMEWORK-LEVEL. `mintQrGeoPose` was already shared, but the
* two things wrapped around it — the frame composition and the level
* assembly — lived in the tour viewer. A second authoring surface (the
* recorder, minting from a whole recording) would otherwise copy both
* verbatim, and the composition is the single easiest thing in this stack to
* get wrong.
*
* FRAME CONTRACT — read this before touching {@link qrWorldPoseFromOdom}.
* The input pose is RAW WebXR/odometry, which is what the tracking controller
* composes with each frame's capture pose. The GPS-world NUE pose is therefore
*
*     alignment · WEBXR_TO_NUE · pose        (basis factor LEADING)
*
* A **trailing** basis factor is correct for a different input: replayed
* STATE, whose quaternions are already basis-conjugated (`R_nue =
* B·R_webxr·B⁻¹`, so `A·B·R_webxr = A·R_nue·B`). That is why the
* capture-time geo join composes the other way round — and applying its form
* here would yaw every anchor by 90°, which is precisely the bug that
* milestone's review caught. The unit tests pin the direction by bearing, not
* by matrix components, because component assertions on a near-identity
* rotation are what let the original bug through.
*
* SEE `GpsPlusSlamJs_Docs/docs/2026-08-28-0636-recorder-qr-anchor-authoring-plan.md`
* §3 M-A / M-C (cold-review blocker 1).
*/
/**
* Default printed side length (m) to prefill in an authoring panel.
*
* 0.18: the largest whole centimetre the print PDF (`qr-print-pdf.ts`)
* places on A4 and on Letter - the owner's "as large as possible" (Tour
* Viewer polish round, P7). The PDF's margin is for ink and the quiet zone
* may reach into it, which is what makes 18 cm fit a 21 cm sheet; 0.2 m
* does not fit. It was 0.16 while the code could also be printed from the
* browser's own dialog, whose margins are not ours
* (`MAX_HOME_PRINTABLE_SIDE_M`); the Tour Viewer prints by PDF only since
* M0b. A test holds it under the PDF ceiling of both papers.
*/
const AUTHOR_DEFAULT_SIZE_M = .18;
/**
* The mint gate's alignment requirement: a non-null alignment matrix is
* VACUOUS — the store ships an IDENTITY matrix from the very first GPS fix,
* and an identity-composed mint stamps a heading that is wrong by the
* session's arbitrary WebXR yaw. Requiring several solved-in GPS fixes is the
* cheap honest floor; field numbers may raise it.
*/
const MIN_ALIGNMENT_SAMPLES = 3;
/**
* Compose a RAW WebXR/odometry pose into the GPS-world NUE frame.
*
* @param odomPose the solved QR pose in raw WebXR/odometry space
* @param alignmentMatrix column-major odom-NUE → GPS-world NUE (the solved
*   TARGET matrix, never a lerped visual transform — for minting, the
*   converged solve is the honest frame)
*/
function qrWorldPoseFromOdom(odomPose, alignmentMatrix) {
	const poseMatrix = new Matrix4().compose(new Vector3(...odomPose.position), new Quaternion(...odomPose.rotation), new Vector3(1, 1, 1));
	const world = new Matrix4().fromArray(alignmentMatrix).multiply(WEBXR_TO_NUE).multiply(poseMatrix);
	const position = new Vector3();
	const rotation = new Quaternion();
	world.decompose(position, rotation, new Vector3());
	rotation.normalize();
	return {
		position: {
			x: position.x,
			y: position.y,
			z: position.z
		},
		rotation: [
			rotation.x,
			rotation.y,
			rotation.z,
			rotation.w
		]
	};
}
/**
* Compose the GPS-world pose and assemble the exportable level.
*
* Refuses in plain words while the session has no usable GPS alignment —
* minting earlier would stamp a garbage anchor behind a printed code. Never
* throws: a caller is a UI panel or a zip contributor, and both want a
* message rather than an exception.
*/
function mintQrLevel(input) {
	const { odomPose, alignmentMatrix, zero, alignment, sizeM, nowIso } = input;
	if (alignmentMatrix === null) return NO_ALIGNMENT_RESULT;
	return mintQrLevelFromWorld({
		world: qrWorldPoseFromOdom(odomPose, alignmentMatrix),
		zero,
		alignment,
		sizeM,
		nowIso,
		...input.quality !== void 0 ? { quality: input.quality } : {}
	});
}
/** The one refusal message both entry points share. */
const NO_ALIGNMENT_RESULT = {
	ok: false,
	error: "No usable GPS alignment yet — walk a few metres with GPS reception, then mint once the pose is stable."
};
/**
* Assemble the level from a pose that is ALREADY in the GPS-world frame.
*
* Split out for the session mint, which combines many sightings into one
* world pose before it gets here — so the composition happens per sighting,
* not once at the end.
*/
function mintQrLevelFromWorld(input) {
	const { world, zero, alignment, sizeM, nowIso } = input;
	if (zero === null || alignment.sampleCount < 3) return NO_ALIGNMENT_RESULT;
	try {
		const level = {
			version: 1,
			qr: {
				physicalSizeM: sizeM,
				geo: mintQrGeoPose({
					worldNuePosition: world.position,
					worldNueRotation: world.rotation,
					zero
				}),
				mintQuality: {
					mintedAtIso: nowIso,
					alignmentSampleCount: alignment.sampleCount,
					...alignment.gpsAccuracyM !== void 0 && Number.isFinite(alignment.gpsAccuracyM) && alignment.gpsAccuracyM > 0 ? { gpsAccuracyM: alignment.gpsAccuracyM } : {},
					...input.quality ?? {}
				}
			}
		};
		return {
			ok: true,
			level,
			json: serializeQrLevel(level)
		};
	} catch (err) {
		return {
			ok: false,
			error: err instanceof Error ? err.message : String(err)
		};
	}
}
//#endregion
export { AUTHOR_DEFAULT_SIZE_M, MIN_ALIGNMENT_SAMPLES, mintQrLevel, mintQrLevelFromWorld, qrWorldPoseFromOdom };
